// src/pages/hub/WorkPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// ─── Contexts ────────────────────────────────────────────────────────────
import { useView } from "../../contexts/ViewContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useNotification } from "../../contexts/NotificationContext";
import { useToast } from "../../contexts/ToastContext";

// ─── Shell / kit / shared ────────────────────────────────────────────────
import { HubShell } from "../../components/hub/shell";
import { PanelErrorBoundary, ErrorState } from "../../components/hub/kit";
import { sanitizePanelError } from "../../components/shared/PanelErrorBoundary";
import ThinScroll from "../../components/shared/ThinScroll";
import { fmtCount } from "../../components/shared/format";

// ─── API (detail/archive/subtask/undo/secondary-fetch logic moved to hooks) ─
import {
  getWorkSearch,
  getSavedViews,
  createWork,
  updateWork,
  upsertSavedView,
  deleteSavedView,
  recurringFromTask,
  recurringList,
} from "../../api/hub/work";

// ─── Work page logic hooks ───────────────────────────────────────────────
import useWorkBucketCache from "../../components/hub/work/hooks/useWorkBucketCache";
import useWorkCharts from "../../components/hub/work/hooks/useWorkCharts";
import useWorkBulkActions from "../../components/hub/work/hooks/useWorkBulkActions";
import useWorkPageFetches from "../../components/hub/work/hooks/useWorkPageFetches";
import useWorkUndo from "../../components/hub/work/hooks/useWorkUndo";
import useWorkSubtasks from "../../components/hub/work/hooks/useWorkSubtasks";
import useWorkDetail from "../../components/hub/work/hooks/useWorkDetail";

// ─── Work constants (root-level, shared across panels) ───────────────────
import {
  BUCKETS,
  DEFAULT_BUCKET,
  GROUP_BY_OPTIONS,
  getGroupDisplayName,
  PRIORITY_OPTIONS,
  getSortComparator,
} from "../../components/hub/work/constants";

// ─── Verdict / charts ────────────────────────────────────────────────────
import VerdictBanner from "../../components/hub/work/verdict/VerdictBanner";
import TakeawayCard from "../../components/hub/work/verdict/TakeawayCard";
import ChartsSection from "../../components/hub/work/verdict/ChartsSection";

// ─── Task list ───────────────────────────────────────────────────────────
import TaskListView from "../../components/hub/work/list/TaskListView";
import SearchMessagesSection from "../../components/hub/work/list/SearchMessagesSection";

// ─── Filters ─────────────────────────────────────────────────────────────
import WorkFilters from "../../components/hub/work/filters/WorkFilters";
import WorkBucketTabs from "../../components/hub/work/filters/WorkBucketTabs";
import ActiveFilterChips from "../../components/hub/work/filters/ActiveFilterChips";

// ─── Slide-out ───────────────────────────────────────────────────────────
import TaskSlideOut from "../../components/hub/work/slideout/TaskSlideOut";

// ─── Overlays (all popovers / modals / client slide-out / drill / toasts) ─
import WorkOverlays from "../../components/hub/work/overlays/WorkOverlays";


// ═══════════════════════════════════════════════════════════════════════════════════
// Main page component
// ═══════════════════════════════════════════════════════════════════════════════════
const WorkPage = () => {
  // ─── Auth / identity ────────────────────────────────────────────────────────
  const view = useView();
  const isClient = view.isClient;
  const clientId = view.supabaseClientId;
  // team member ID space (ops_team_members.id), not the auth user ID
  // (auth.users.id). Task owner_id references ops_team_members.id, so the
  // done-bucket p_owner_id and the new-task "is this mine?" comparison must
  // both use this ID. See work-page-fixes.md §A Item 1.
  const currentUserId = view.teamMemberId;

  const { showSuccess, showError } = useNotification();
  const { toastSuccess, toastError } = useToast();
  const { can } = useWorkspace();
  const canSeeCharts = can("effort.read.all");

  // ─── URL state ──────────────────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === "") next.delete(key);
      else next.set(key, String(value));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bucketParam = searchParams.get("bucket");
  const activeBucket = (BUCKETS.find((b) => b.key === bucketParam) || bucketParam === "recurring") ? bucketParam : DEFAULT_BUCKET;
  const setActiveBucket = useCallback((b) => {
    updateParam("bucket", b);
    setSearchQuery("");
    // "Unowned" (nobody owns it) and "Mine" / waiting_on_me (current user owns
    // it) fix ownership structurally, so an owner filter is contradictory there.
    // Clear any stale owner filter on the switch so it can't persist (and stays
    // consistent with the disabled owner dropdown in <WorkFilters>). Mirrors the
    // existing setSearchQuery clear above (work-page-fixes §B).
    if (b === "unowned" || b === "waiting_on_me") setFilterOwner(null);
  }, [updateParam]);

  const handleSelectBucket = useCallback((b) => {
    setActiveBucket(b);
  }, [setActiveBucket]);

  const selectedTaskId = searchParams.get("task") || null;
  const setSelectedTask = useCallback((id) => updateParam("task", id), [updateParam]);

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [retryNonce, setRetryNonce] = useState(0);
  const [verdictDismissed, setVerdictDismissed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCharts, setShowCharts] = useState(true);
  const [slideOutClientId, setSlideOutClientId] = useState(null);
  const [drillState, setDrillState] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [rowAssignTarget, setRowAssignTarget] = useState(null);
  const [rowMoveTarget, setRowMoveTarget] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  // ─── Filter state ───────────────────────────────────────────────────────────
  const [groupBy, setGroupBy] = useState("none");
  const [sortField, setSortField] = useState("urgency");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filterOwner, setFilterOwner] = useState(null);
  const [filterService, setFilterService] = useState(null);
  const [filterPipeline, setFilterPipeline] = useState(null);
  // Client filter — named clientFilter (not clientId) to avoid shadowing the
  // workspace-scope `clientId` (view.supabaseClientId) above. Passed to
  // <WorkFilters> as the `clientId`/`setClientId` props and to
  // useWorkBucketCache as `clientFilter`, which forwards it to fn_work_list
  // as p_client_id. null = no client filter (returns all clients).
  const [clientFilter, setClientFilter] = useState(null);
  // "Include done" is scoped per bucket tab (work-page-fixes §C). Each bucket
  // remembers its own toggle, so switching tabs reads that bucket's value and
  // the done supplement only fires for the bucket whose toggle is on. The
  // derived `includeDone` (current bucket) keeps the same name/type (boolean)
  // for all consumers; `setIncludeDone` updates only the active bucket's entry.
  const [includeDoneByBucket, setIncludeDoneByBucket] = useState({});
  const includeDone = includeDoneByBucket[activeBucket] || false;
  const setIncludeDone = useCallback(
    (value) => setIncludeDoneByBucket((prev) => ({ ...prev, [activeBucket]: value })),
    [activeBucket]
  );
  // Direct-render recurring items (work-page-fixes Item 1 rewrite). The
  // Recurring tab now renders fn_recurring_list rows directly instead of
  // fetching fn_work_list and filtering client-side by an id Set (fn_work_list
  // caps at 200 items, so the recurring task was often missing). Each rule is
  // mapped into the item shape InboxRow renders.
  const [recurringItems, setRecurringItems] = useState([]);
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchMessages, setSearchMessages] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const toggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // ─── Scroll-to-row after task creation (Item 3) ──────────────────────────────
  // After a new task is created we switch bucket + open the slide-out, then set
  // this id so the new row scrolls into view once the bucket fetch renders. The
  // scroll effect lives below (after `activeCache` is in scope) and watches
  // activeCache.items rather than a fixed timeout — see "Scroll-to-row effect".
  const [scrollToTaskId, setScrollToTaskId] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════════
  // Hooks — data fetching
  // ═══════════════════════════════════════════════════════════════════════════════

  const {
    bucketCache,
    setBucketCache,
    cacheKey,
    listError,
    setListError,
    activeCache,
    workTypeOptions,
    patchItem,
    removeItem,
    removeItems,
  } = useWorkBucketCache({
    clientId,
    activeBucket,
    groupBy,
    filterOwner,
    filterService,
    filterPipeline,
    clientFilter,
    includeDone,
    debouncedSearch,
    retryNonce,
    currentUserId,
  });

  // ─── Scroll-to-row effect (Item 4) ──────────────────────────────────────────
  // Watch activeCache.items instead of a fixed timeout — the bucket fetch is
  // async, so a hard-coded delay is unreliable. Only scroll once the new task's
  // row is present in the fetched items, which guarantees the row is in the DOM
  // before we query it. Clears scrollToTaskId after scrolling so it doesn't
  // fire again on the next render.
  useEffect(() => {
    if (!scrollToTaskId) return;
    if (!activeCache.items?.some((i) => i.id === scrollToTaskId)) return;
    const id = scrollToTaskId;
    const el = document.querySelector(`[data-task-id="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setScrollToTaskId(null);
  }, [scrollToTaskId, activeCache.items]);

  const { throughputData, positionData, waitingData, chartsLoading } =
    useWorkCharts(clientId);

  // ─── Secondary fetches (verdict / takeaways / saved views / roster / load) ──
  const {
    verdict,
    verdictLoading,
    visibleTakeaways,
    dismissTakeaway,
    loadData,
    savedViews,
    setSavedViews,
    teamMembers,
  } = useWorkPageFetches({ clientId, clientFilter, retryNonce });

  // ─── Undo toast ────────────────────────────────────────────────────────────
  const { undoToast, showUndoToast, handleRestore } = useWorkUndo({
    activeBucket,
    setBucketCache,
    setRetryNonce,
    toastError,
  });

  // ─── Subtask expansion ─────────────────────────────────────────────────────
  const {
    expandedSubtasks,
    subtaskCache,
    subtaskRevision,
    setSubtaskRevision,
    handleToggleSubtasks,
    handleAddSubtask,
    handleDeleteSubtask,
    handleSubtaskCountChange,
  } = useWorkSubtasks({ patchItem, setBucketCache, setRetryNonce, toastError });

  // ─── Task detail + slide-out write handlers ────────────────────────────────
  // Must come after useWorkUndo (showUndoToast) and useWorkSubtasks
  // (setSubtaskRevision) — both are inputs.
  const {
    detail,
    setDetail,
    refreshDetailSilent,
    handlePositionClick,
    handleTitleChange,
    handlePriorityChange,
    handleServiceChange,
    handleWorkTypeChange,
    handleClientChange,
    handleSlideOutDueDateChange,
    handleDescriptionChange,
    handleSlideOutTaskUpdated,
    handleSlideOutDelete,
    handleArchive,
    handleRowArchive,
    handleRowDelete,
  } = useWorkDetail({
    selectedTaskId,
    setSelectedTask,
    includeDone,
    patchItem,
    removeItem,
    setRetryNonce,
    setSubtaskRevision,
    setDebouncedSearch,
    setSearchQuery,
    showUndoToast,
    toastError,
    toastSuccess,
  });

  // ═══════════════════════════════════════════════════════════════════════════════
  // Derived state — MUST come before useWorkBulkActions (which reads `groups`)
  // ═══════════════════════════════════════════════════════════════════════════════

  const counts = useMemo(() => {
    const allCounts = Object.values(bucketCache).reduce((acc, entry) => {
      if (entry?.counts) {
        Object.entries(entry.counts).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
      }
      return acc;
    }, {});
    return Object.keys(allCounts).length > 0 ? allCounts : {};
  }, [bucketCache]);

  // `counts` is own-only per bucket (done is never stored in entry.counts —
  // see useWorkBucketCache §B). Add the active bucket's filtered done count
  // (kept separately on activeCache.doneCount) only when includeDone is on, so
  // the header total reflects done without accumulating it across bucket
  // switches. doneCount lives solely on the active entry, which is replaced on
  // every fetch, so it is added exactly once — never once-per-visited-bucket.
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
    + (includeDone ? (activeCache.doneCount ?? 0) : 0);

  // ─── Total count freeze (Item 5) ────────────────────────────────────────────
  // Preserve the last known total across grouping transitions. When the
  // group-by filter changes, bucketCache evicts the old entry and fetches a
  // new one, so `counts` (and thus `total`) drop to 0 mid-transition. Rendering
  // lastTotalRef.current || total keeps the previous total on screen instead of
  // flashing 0/blank. The ref updates after each render where total is truthy.
  const lastTotalRef = useRef(0);
  useEffect(() => {
    if (total) lastTotalRef.current = total;
  }, [total]);
  const isLoading = activeCache.loading;

  const groups = useMemo(() => {
    if (!activeCache.items || activeCache.items.length === 0) return null;
    const option = GROUP_BY_OPTIONS.find((opt) => opt.key === groupBy);
    if (!option || !option.groupFn) return null;
    const fn = option.groupFn;
    const comparator = getSortComparator(sortField, sortDirection);
    const baseItems = activeCache.items;
    const seen = new Set();
    const order = [];
    for (const item of baseItems) {
      const key = fn(item);
      if (key == null) continue;
      if (!seen.has(key)) { seen.add(key); order.push(key); }
    }
    return order.map((rawKey) => ({
      key: rawKey,
      displayName: getGroupDisplayName(groupBy, rawKey),
      // Sort each group's items by the active sort field. When group-by is on,
      // every group is sorted independently. .slice() avoids mutating the cache.
      items: baseItems
        .filter((item) => fn(item) === rawKey)
        .slice()
        .sort(comparator),
    }));
  }, [activeCache.items, groupBy, sortField, sortDirection]);

  // Flat-list items, re-sorted by the active sort field. The bucket cache hook
  // already urgency-sorts activeCache.items, so when sortField is "urgency" this
  // re-sort is a no-op (identical order — preserves the existing default). For
  // any other sort field we re-sort here so the flat (ungrouped) list honors it.
  // .slice() avoids mutating the cached array; only TaskListView consumes this.
  const sortedActiveCache = useMemo(
    () => ({
      ...activeCache,
      items: (activeCache.items || [])
        .slice()
        .sort(getSortComparator(sortField, sortDirection)),
    }),
    [activeCache, sortField, sortDirection]
  );

  // ─── Recurring flag for the open slide-out (work-page-fixes §A Item 1) ──────
  // fn_work_detail does NOT return `recurring` on the task object, but the list
  // items from fn_work_list DO carry `recurring: true/false`. The slide-out's
  // repeating badge reads `task.recurring`, so look the open task up across
  // EVERY bucket cache (so it works no matter which bucket/search it came from,
  // including the done-bucket supplement when "Include done" is on) and pass the
  // flag down to the slide-out. When the task isn't in any cache yet (e.g. a
  // deep link before the bucket has loaded) this is undefined and the badge
  // stays hidden — the backend note (Item 2) covers adding `recurring` to
  // fn_work_detail so deep links work without a list fetch.
  const selectedRecurring = useMemo(() => {
    if (!selectedTaskId) return undefined;
    for (const entry of Object.values(bucketCache)) {
      const found = (entry?.items || []).find((it) => it.id === selectedTaskId);
      if (found) return found.recurring === true;
    }
    return undefined;
  }, [bucketCache, selectedTaskId]);

  // Default large groups (>50 items) to collapsed
  const defaultsSetForRef = useRef(null);
  useEffect(() => {
    if (defaultsSetForRef.current === groupBy) return;
    if (!groups || groups.length === 0) return;
    defaultsSetForRef.current = groupBy;
    const next = {};
    for (const g of groups) { if ((g.items || []).length > 50) next[g.key] = true; }
    setCollapsedGroups(next);
  }, [groupBy, groups]);

  const allFilterOptions = useMemo(() => {
    const allItems = Object.values(bucketCache).flatMap((b) => b?.items || []);
    const ownerMap = new Map();
    allItems.forEach((i) => {
      if (i.owner_id && i.owner && !ownerMap.has(i.owner_id)) ownerMap.set(i.owner_id, i.owner);
    });
    const owners = [...ownerMap.entries()].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const serviceMap = new Map();
    allItems.forEach((i) => {
      const code = i.service_code || i.service;
      const label = i.service || i.service_code;
      if (code && !serviceMap.has(code)) serviceMap.set(code, label);
    });
    const services = [...serviceMap.entries()].map(([code, label]) => ({ code, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const clientMap = new Map();
    allItems.forEach((i) => {
      if (i.client_id && i.client && !clientMap.has(i.client_id)) clientMap.set(i.client_id, i.client);
    });
    const clients = [...clientMap.entries()].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const pipelines = [...new Set(allItems.map((i) => i.work_type).filter(Boolean))].sort();
    return { owners, services, clients, pipelines };
  }, [bucketCache]);

  const existingTags = useMemo(() => {
    const tagSet = new Set();
    Object.values(bucketCache).forEach((bucket) => {
      bucket?.items?.forEach((item) => {
        if (!item.tags) return;
        let parsed = item.tags;
        if (typeof parsed === "string") {
          try { parsed = JSON.parse(parsed); }
          catch { parsed = parsed.split(",").map((t) => t.trim()).filter(Boolean); }
        }
        if (Array.isArray(parsed)) parsed.forEach((t) => tagSet.add(t));
      });
    });
    return [...tagSet].sort();
  }, [bucketCache]);

  const hasActiveFilters = Boolean(
    clientFilter || filterOwner || filterService || filterPipeline || includeDone || debouncedSearch
  );
  const getOwnerName = (id) => {
    if (!id) return "";
    // fn_work_list returns an owner_id but often not the display name, so the
    // bucket-derived allFilterOptions.owners can't always resolve an owner — the
    // owner-filter pill used to fall back to the raw UUID (QA work-page-fixes §B).
    // Resolve against the team roster (ops_team_members → {id,name}, the canonical
    // source) first, then the bucket-derived list (covers former members / agents
    // still owning loaded tasks), then a readable fallback — never a raw UUID.
    // Reused by the assignment patchItem calls in WorkOverlays.
    const rosterHit = teamMembers.find((m) => m.id === id)?.name;
    if (rosterHit) return rosterHit;
    const bucketHit = allFilterOptions.owners.find((o) => o.id === id)?.name;
    if (bucketHit) return bucketHit;
    return "Unknown";
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // Hooks — bulk actions (now safe — `groups` is defined above)
  // ═══════════════════════════════════════════════════════════════════════════════

  const {
    selectedIds,
    setSelectedIds,
    toggleSelect,
    clearSelection,
    handleBulkAction: dispatchBulkAction,
    handleBulkMove,
    handleRowMoveConfirm,
    handleBulkDelete,
    handleBulkArchive,
  } = useWorkBulkActions({
    bucketCache,
    activeCache,
    groups,
    removeItems,
    setRetryNonce,
    onClearSearch: () => { setDebouncedSearch(""); setSearchQuery(""); },
    showError,
  });

  // ─── Select all visible (toggles) ───────────────────────────────────────────
  // Accepts the list of currently-visible item IDs (computed by TaskListView,
  // which respects collapsed groups). If every visible id is already selected,
  // clear the selection; otherwise select exactly the visible ids. Keeps the
  // toggle logic out of the bulk-actions hook so it can stay view-agnostic.
  const selectAllVisible = useCallback((ids) => {
    if (!ids || ids.length === 0) return;
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  }, [setSelectedIds]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // Effects
  // ═══════════════════════════════════════════════════════════════════════════════

  // ─── Debounce search ────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ─── Free-text search ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!debouncedSearch) { setSearchResults(null); setSearchMessages([]); setIsSearchLoading(false); return; }
    let cancelled = false;
    setIsSearchLoading(true);
    getWorkSearch(debouncedSearch)
      .then((resp) => {
        if (!cancelled) {
          setSearchResults(resp?.data?.work || []);
          setSearchMessages(resp?.data?.messages || []);
        }
      })
      .catch(() => { if (!cancelled) { setSearchResults([]); setSearchMessages([]); } })
      .finally(() => { if (!cancelled) setIsSearchLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedSearch]);

  // ─── Recurring tab direct render (work-page-fixes Item 1 rewrite) ───────────
  // When the "Recurring" tab is active, fetch every recurring rule via
  // recurringList() and map each rule into the item shape that InboxRow renders.
  // This replaces the old approach of fetching fn_work_list and filtering
  // client-side by an id Set — fn_work_list caps at 200 items, so the recurring
  // task was often missing. Skip rules whose open_copy is null. When the tab is
  // not active, clear recurringItems to [].
  useEffect(() => {
    if (activeBucket !== "recurring") { setRecurringItems([]); return; }
    let cancelled = false;
    setRecurringLoading(true);
    recurringList(null)
      .then((resp) => {
        if (cancelled) return;
        const rules = Array.isArray(resp?.data) ? resp.data : [];
        const items = [];
        for (const rule of rules) {
          if (!rule?.open_copy) continue;
          items.push({
            id: rule.open_copy.id,
            title: rule.open_copy.title,
            client: rule.client,
            owner: rule.owner,
            due: rule.open_copy.due,
            status: rule.status_label,
            bucket: "recurring",
            recurring: true,
          });
        }
        setRecurringItems(items);
        setRecurringLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setRecurringItems([]);
        setRecurringLoading(false);
        toastError(err?.message || "Could not load repeating tasks");
      });
    return () => { cancelled = true; };
  }, [activeBucket, toastError]);

  // ─── Close task slide-out on Escape ──────────────────────────────────────────
  useEffect(() => {
    if (!selectedTaskId) return;
    const handler = (e) => { if (e.key === "Escape") setSelectedTask(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedTaskId]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // Handlers — bulk actions (bridge from hook to UI state)
  // ═══════════════════════════════════════════════════════════════════════════════

  const handleBulkAction = useCallback((action) => {
    dispatchBulkAction(action, { setAssignTarget, setMoveTarget, setDeleteTarget });
  }, [dispatchBulkAction, setAssignTarget, setMoveTarget, setDeleteTarget]);

  const handleBulkMoveConfirm = useCallback(async (statusCode) => {
    if (!moveTarget?.length) return;
    await handleBulkMove(moveTarget, statusCode);
    setMoveTarget(null);
  }, [moveTarget, handleBulkMove]);

  const handleRowMoveConfirmFinal = useCallback(async (statusCode) => {
    if (!rowMoveTarget?.taskId) return;
    await handleRowMoveConfirm(rowMoveTarget.taskId, statusCode);
    setRowMoveTarget(null);
  }, [rowMoveTarget, handleRowMoveConfirm]);

  const confirmBulkDelete = useCallback(async () => {
    if (!deleteTarget?.ids) return;
    await handleBulkDelete(deleteTarget.ids, {
      onUndo: (ids) => showUndoToast(ids),
      onArchiveRefused: (ids) => setArchiveTarget(ids),
      onDone: () => {
        setDeleteTarget(null);
      },
    });
  }, [deleteTarget, handleBulkDelete, showUndoToast]);

  const confirmBulkArchive = useCallback(async (ids) => {
    await handleBulkArchive(ids);
    setDeleteTarget(null);
    setArchiveTarget(null);
  }, [handleBulkArchive]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // Handlers — row-level UI glue
  // ═══════════════════════════════════════════════════════════════════════════════

  const handleSlideOutAssigneeClick = useCallback(() => {
    const t = detail.data?.task || {};
    setRowAssignTarget({
      taskId: selectedTaskId,
      clientId: t.client_id,
      workType: t.work_type,
      taskTitle: t.title,
    });
  }, [selectedTaskId, detail.data]);

  const handleRowAssign = (item) => {
    setRowAssignTarget({ taskId: item.id, clientId: item.client_id, workType: item.work_type, taskTitle: item.title });
  };
  const handleRowMove = (item) => { setRowMoveTarget({ taskId: item.id }); };
  const handleRowEdit = (item) => { setEditingTask(item); };
  const handleAssigneeClick = (item) => {
    setRowAssignTarget({ taskId: item.id, clientId: item.client_id, workType: item.work_type, taskTitle: item.title });
  };

  const handleDueDateChange = async (item, newDate) => {
    try {
      await updateWork({ task_id: item.id, due: newDate, clear_due: !newDate });
      patchItem(item.id, { due: newDate || null });
      setRetryNonce((n) => n + 1);
    } catch (err) { toastError(`Update failed: ${err?.message || "Unknown"}`, item.id, "due_date_change"); }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // Handlers — filter / drill / create / presets
  // ═══════════════════════════════════════════════════════════════════════════════

  const handleClearFilters = () => {
    setFilterOwner(null); setFilterService(null); setFilterPipeline(null);
    setClientFilter(null);
    setIncludeDone(false); setSearchQuery("");
  };

  const handleVerdictFocus = useCallback(() => {
    const focus = String(verdict?.focus || "").toLowerCase();
    if (focus.includes("unowned") || focus.includes("no owner") || focus.includes("without an owner")) {
      setActiveBucket("unowned");
    }
    document.getElementById("work-bucket-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [verdict, setActiveBucket]);

  const openDrill = useCallback(
    (what, id) => setDrillState({
      what, id,
      scope: { clientId, bucket: activeBucket, groupBy, owner: filterOwner, service: filterService, workType: filterPipeline },
    }),
    [clientId, activeBucket, groupBy, filterOwner, filterService, filterPipeline]
  );
  const closeDrill = () => setDrillState(null);

  const handleCreateTask = async (payload, repeatSettings) => {
    try {
      const resp = await createWork(payload);
      if (resp?.data?.error) {
        toastError(resp.data.message || "Failed to create task", resp?.data?.id ?? null, "create");
        return;
      }
      setRetryNonce((n) => n + 1);
      toastSuccess("Task created", resp?.data?.id ?? null, "create");

      // Extract the new task's ID. fn_work_create may return the UUID
      // directly, an object with an `id`, or an array whose first element
      // is that object — handle all three shapes.
      const created = resp?.data;
      const newTaskObj = Array.isArray(created) ? created[0] : created;
      const newTaskId =
        typeof newTaskObj === "string"
          ? newTaskObj
          : newTaskObj?.id ?? null;

      if (newTaskId) {
        // Derive the bucket the new task lands in from its owner + status.
        // `currentUserId` is the current user's team member id (view.teamMemberId
        // = ops_team_members.id). Precedence: a client-waiting status wins (the
        // ball is in the client's court regardless of owner), then owner-based.
        // This corrects the known fn_work_bucket bug that misclassifies null
        // owners as "Mine".
        const newStatus = newTaskObj?.status ? String(newTaskObj.status) : "";
        const isClientWaiting =
          newStatus === "waiting_on_client" || newStatus === "no_response";
        const ownerId = newTaskObj?.owner_id || null;
        const derivedBucket = isClientWaiting
          ? "waiting_on_them"
          : !ownerId
            ? "unowned"
            : ownerId === currentUserId
              ? "waiting_on_me"
              : "waiting_on_us";
        // Evict the derived bucket's cache entries so the retryNonce bump
        // above forces a fresh fetch instead of hitting the cache. The
        // cache-hit guard in useWorkBucketCache short-circuits the fetch when
        // bucketCache[cacheKey]?.items?.length > 0 — without this eviction it
        // reuses the STALE entry from the last time this bucket was viewed, so
        // the newly created task never appears until a manual page refresh
        // (work-page-fixes.md §B Item 4). The colon in the startsWith match
        // scopes to the bucket segment of the `${bucket}:${filterSignature}`
        // cache key.
        setBucketCache((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((key) => {
            if (key.startsWith(`${derivedBucket}:`)) delete next[key];
          });
          return next;
        });
        // Order matters: switch the bucket first so its cache refetch is in
        // flight before the slide-out detail fetch kicks in.
        setActiveBucket(derivedBucket);
        setSelectedTask(newTaskId);
        // Signal the scroll effect to bring the new row into view once the
        // bucket fetch has rendered.
        setScrollToTaskId(newTaskId);
      }
      // Return the new task id so callers (e.g. CreateTaskModal's Files
      // upload flow) can attach files to the just-created task.
      // If the modal requested repeating (work-page-fixes §B), promote the
      // new task into a recurring rule via fn_recurring_from_task. A failure
      // here does NOT roll back the task — the task is already created and
      // saved; only the repeating setup failed, so the user is told but the
      // task remains.
      if (newTaskId && repeatSettings?.cadence) {
        try {
          await recurringFromTask(
            newTaskId,
            repeatSettings.cadence,
            repeatSettings.dayOfWeek ?? null,
            repeatSettings.dayOfMonth ?? null,
          );
          toastSuccess("Task created and set to repeat");
        } catch (e) {
          toastError(
            e?.message || "Task created, but repeating could not be set up",
          );
        }
      }
      return newTaskId;
    } catch (err) {
      console.error("Failed to create task:", err);
      toastError("Failed to create task", null, "create");
    }
  };

  const handleSavePreset = async (name) => {
    const filters = { p_owner_id: filterOwner || null, p_service: filterService || null, p_work_type: filterPipeline || null, p_include_done: includeDone || false };
    try {
      await upsertSavedView({ name, filters, surface: "work" });
      const resp = await getSavedViews();
      setSavedViews(resp?.data || []);
      toastSuccess("Preset saved");
    } catch (err) { toastError(err?.message || "Save preset failed"); }
  };

  const handleDeletePreset = async (viewId) => {
    try {
      await deleteSavedView(viewId);
      setSavedViews((prev) => prev.filter((v) => v.id !== viewId));
      setConfirmingDeleteId(null);
      toastSuccess("Preset deleted");
    } catch (err) { toastError(err?.message || "Delete preset failed"); }
  };

  // Parameter named `id` (not clientId) so it never shadows the workspace-scope
  // clientId (view.supabaseClientId) declared at the top of the file.
  const handleClientClick = (id) => setSlideOutClientId(id);

  // Direct navigation for takeaway cards — replaces the old DrillSheet click.
  // Maps the takeaway's `drill` ({ what, id }) to a concrete page action.
  const handleTakeawayNavigate = useCallback(
    (drill) => {
      if (!drill?.what) return;
      // After a bucket switch, smooth-scroll to the bucket tabs so the user
      // lands near the relevant rows (matches the handleVerdictFocus gesture).
      const scrollToBuckets = () =>
        document.getElementById("work-bucket-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" });
      switch (drill.what) {
        case "bucket":
          if (drill.id) setActiveBucket(drill.id);
          scrollToBuckets();
          break;
        case "client_waiting":
          setActiveBucket("waiting_on_them");
          scrollToBuckets();
          break;
        case "past_p90":
          setActiveBucket("unowned");
          scrollToBuckets();
          break;
        case "blocked":
        case "stalled":
          setActiveBucket("waiting_on_us");
          scrollToBuckets();
          break;
        default:
          break;
      }
    },
    [setActiveBucket]
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <HubShell>
      <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">

        {/* ─── Page header ──────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Work</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Who is this waiting on — the only question that matters on a Tuesday.
            </p>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto self-center">{fmtCount(lastTotalRef.current || total)} tasks</span>
        </div>

        {/* ─── RPC error ───────────────────────────────────────────────── */}
        {!isLoading && activeCache.error && (
          <ErrorState
            variant="card"
            title="Failed to load work items"
            message={sanitizePanelError(activeCache.error) || "Couldn't load your work items. Please try again."}
            onRetry={() => setRetryNonce((n) => n + 1)}
          />
        )}

        {/* ─── Loaded content ─────────────────────────────────────────── */}
        {!activeCache.error && (
          <>
            {/* ─── Verdict + Takeaways ──────────────────────────────────── */}
            <PanelErrorBoundary label="Verdict">
              {!verdictDismissed && (verdict || verdictLoading) && (
                <VerdictBanner
                  verdict={verdict}
                  isLoading={verdictLoading}
                  onDismiss={() => setVerdictDismissed(true)}
                  onFocusClick={handleVerdictFocus}
                  openDrill={openDrill}
                />
              )}
              {visibleTakeaways.length > 0 && (
                <div className="border-b border-gray-200 dark:border-gray-800 pb-3 mb-3">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Key Takeaways</h3>
                  <ThinScroll className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory">
                    {visibleTakeaways.map((tw, i) => (
                      <div key={i} className="min-w-[200px] max-w-[240px] shrink-0 snap-start">
                        <TakeawayCard takeaway={tw} onClientClick={handleClientClick} onNavigate={handleTakeawayNavigate} onDismiss={dismissTakeaway} />
                      </div>
                    ))}
                  </ThinScroll>
                </div>
              )}
            </PanelErrorBoundary>

            {/* ─── Load strip ────────────────────────────────────────── */}
            {loadData && (
              <PanelErrorBoundary label="Load strip">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {loadData.overdue} overdue · {loadData.no_date} without a due date · {loadData.total_open} total open
                </p>
              </PanelErrorBoundary>
            )}

            {/* ─── Charts + total count ──────────────────────────────────── */}
            <PanelErrorBoundary label="Charts">
              <ChartsSection
                canSeeCharts={canSeeCharts}
                showCharts={showCharts}
                onToggle={() => setShowCharts(!showCharts)}
                chartsLoading={chartsLoading}
                throughputData={throughputData}
                positionData={positionData}
                waitingData={waitingData}
                openDrill={openDrill}
              />
            </PanelErrorBoundary>

            {/* ─── Bucket tabs + New task ──────────────────────────────── */}
            <WorkBucketTabs
              counts={counts}
              activeCache={activeCache}
              includeDone={includeDone}
              activeBucket={activeBucket}
              disabled={showCreateModal}
              onSelect={handleSelectBucket}
              onCreate={() => setShowCreateModal(true)}
              recurringCount={recurringItems.length}
            />

            {/* ─── Filter row ────────────────────────────────────────── */}
            <WorkFilters
              filterOwner={filterOwner} setFilterOwner={setFilterOwner}
              filterService={filterService} setFilterService={setFilterService}
              filterPipeline={filterPipeline} setFilterPipeline={setFilterPipeline}
              clientId={clientFilter} setClientId={setClientFilter}
              allFilterOptions={allFilterOptions} workTypeOptions={workTypeOptions}
              groupBy={groupBy} setGroupBy={setGroupBy}
              sortField={sortField} setSortField={setSortField}
              sortDirection={sortDirection} setSortDirection={setSortDirection}
              includeDone={includeDone} setIncludeDone={setIncludeDone}
              searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              savedViews={savedViews} selectedViewId={selectedViewId} setSelectedViewId={setSelectedViewId}
              presetDropdownOpen={presetDropdownOpen} setPresetDropdownOpen={setPresetDropdownOpen}
              confirmingDeleteId={confirmingDeleteId} setConfirmingDeleteId={setConfirmingDeleteId}
              handleDeletePreset={handleDeletePreset} setShowSaveModal={setShowSaveModal}
              hasActiveFilters={hasActiveFilters} handleClearFilters={handleClearFilters}
              setActiveBucket={setActiveBucket}
              activeBucket={activeBucket}
            />

            {/* ─── Active-filter chips ─────────────────────────────────── */}
            {hasActiveFilters && (
              <ActiveFilterChips
                ownerName={filterOwner ? getOwnerName(filterOwner) : ""}
                clientName={clientFilter
                  ? (allFilterOptions.clients.find((c) => c.id === clientFilter)?.name || clientFilter)
                  : ""}
                service={filterService}
                pipeline={filterPipeline}
                includeDone={includeDone}
                search={debouncedSearch}
                onClearOwner={() => setFilterOwner(null)}
                onClearClient={() => setClientFilter(null)}
                onClearService={() => setFilterService(null)}
                onClearPipeline={() => setFilterPipeline(null)}
                onClearDone={() => setIncludeDone(false)}
                onClearSearch={() => setSearchQuery("")}
              />
            )}

            {/* ─── Task list ───────────────────────────────────────────── */}
            <PanelErrorBoundary label="Task list">
              <TaskListView
                activeCache={activeBucket === "recurring" ? { items: recurringItems, loading: recurringLoading } : sortedActiveCache}
                groups={activeBucket === "recurring" ? null : groups}
                collapsedGroups={collapsedGroups}
                toggleGroup={toggleGroup}
                selectedIds={selectedIds}
                toggleSelect={toggleSelect}
                onSelectAll={selectAllVisible}
                clearSelection={clearSelection}
                onBulkAction={handleBulkAction}
                selectedTaskId={selectedTaskId}
                setSelectedTask={setSelectedTask}
                onAssign={handleRowAssign}
                onMove={handleRowMove}
                onArchive={handleRowArchive}
                onEdit={handleRowEdit}
                onDelete={handleRowDelete}
                onAssigneeClick={handleAssigneeClick}
                onDueDateChange={handleDueDateChange}
                onClientClick={handleClientClick}
                activeBucket={activeBucket}
                expandedSubtasks={expandedSubtasks}
                subtaskCache={subtaskCache}
                onToggleSubtasks={handleToggleSubtasks}
                onAddSubtask={handleAddSubtask}
                onDeleteSubtask={handleDeleteSubtask}
                onSubtaskCountChange={handleSubtaskCountChange}
                isLoading={activeBucket === "recurring" ? recurringLoading : isLoading}
                isSearchLoading={isSearchLoading}
                listError={listError}
                hasActiveFilters={hasActiveFilters}
                onRetry={() => { setListError(null); setRetryNonce((n) => n + 1); }}
                setRetryNonce={setRetryNonce}
                subtaskRevision={subtaskRevision}
              />
            </PanelErrorBoundary>

            {/* ─── Search messages ─────────────────────────────────────── */}
            {debouncedSearch && searchMessages.length > 0 && (
              <SearchMessagesSection
                messages={searchMessages}
                onSelectTask={setSelectedTask}
              />
            )}
          </>
        )}
      </div>

      {/* ─── Task slide-out (primary surface stays in the page) ─────────────── */}
      <TaskSlideOut
        selectedTaskId={selectedTaskId}
        detail={detail}
        recurring={selectedRecurring}
        onClose={() => setSelectedTask(null)}
        onClientClick={handleClientClick}
        onPositionClick={(code, label) => handlePositionClick(selectedTaskId, code, label)}
        onTitleChange={(taskId, newTitle) => handleTitleChange(taskId, newTitle)}
        onDueDateChange={(taskId, newDate) => handleSlideOutDueDateChange(taskId, newDate)}
        onDescriptionChange={(taskId, newDescription) => handleDescriptionChange(taskId, newDescription)}
        onAssigneeClick={handleSlideOutAssigneeClick}
        onTaskUpdated={handleSlideOutTaskUpdated}
        onArchive={handleArchive}
        showError={showError}
        showSuccess={showSuccess}
        onPriorityChange={handlePriorityChange}
        onServiceChange={handleServiceChange}
        onWorkTypeChange={handleWorkTypeChange}
        onClientChange={handleClientChange}
        priorityOptions={PRIORITY_OPTIONS}
        serviceOptions={allFilterOptions.services}
        subtaskRevision={subtaskRevision}
        workTypeOptions={workTypeOptions}
        onDelete={handleSlideOutDelete}
      />

      {/* ─── Overlays (popovers, move pickers, confirms, client slide-out,
             drill sheet, modals, undo toast) ────────────────────────────── */}
      <WorkOverlays
        assignTarget={assignTarget}
        rowAssignTarget={rowAssignTarget}
        moveTarget={moveTarget}
        rowMoveTarget={rowMoveTarget}
        deleteTarget={deleteTarget}
        archiveTarget={archiveTarget}
        slideOutClientId={slideOutClientId}
        drillState={drillState}
        showCreateModal={showCreateModal}
        showSaveModal={showSaveModal}
        undoToast={undoToast}
        setAssignTarget={setAssignTarget}
        setRowAssignTarget={setRowAssignTarget}
        setMoveTarget={setMoveTarget}
        setRowMoveTarget={setRowMoveTarget}
        setDeleteTarget={setDeleteTarget}
        setArchiveTarget={setArchiveTarget}
        setSlideOutClientId={setSlideOutClientId}
        setShowCreateModal={setShowCreateModal}
        setShowSaveModal={setShowSaveModal}
        closeDrill={closeDrill}
        confirmBulkDelete={confirmBulkDelete}
        confirmBulkArchive={confirmBulkArchive}
        handleBulkMoveConfirm={handleBulkMoveConfirm}
        handleRowMoveConfirmFinal={handleRowMoveConfirmFinal}
        handleRestore={handleRestore}
        workTypeOptions={workTypeOptions}
        allFilterOptions={allFilterOptions}
        existingTags={existingTags}
        handleCreateTask={handleCreateTask}
        handleSavePreset={handleSavePreset}
        selectedTaskId={selectedTaskId}
        setDetail={setDetail}
        patchItem={patchItem}
        clearSelection={clearSelection}
        getOwnerName={getOwnerName}
        setRetryNonce={setRetryNonce}
        toastError={toastError}
        toastSuccess={toastSuccess}
      />
    </HubShell>
  );
};

export default WorkPage;
